import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings as SettingsIcon, Save, Loader2, Building, FileText, RefreshCw, Users, Plus, Pencil, Trash2, Eye, EyeOff, UserPlus, FolderCog, Sparkles, Calendar } from 'lucide-react';
import { settingsAPI, usersAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import CategoryManagement from '../components/settings/CategoryManagement';
import CustomChargeTemplatesManagement from '../components/settings/CustomChargeTemplatesManagement';
import EventTypesManagement from '../components/settings/EventTypesManagement';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [terms, setTerms] = useState({
    company_name: '',
    company_tagline: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    deposit_percentage: 50,
    deposit_note: '',
    guest_count_note: '',
    cancellation_note: '',
    changes_note: '',
    liability_note: '',
    additional_terms: ''
  });
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  const { toast } = useToast();

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await settingsAPI.getContractTerms();
      setTerms(data);
    } catch (error) {
      toast({ title: 'Error loading settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  
  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, [fetchSettings, fetchUsers]);
  
  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        name: user.name,
        email: user.email,
        password: '' // Don't pre-fill password
      });
    } else {
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '' });
    }
    setShowPassword(false);
    setShowUserDialog(true);
  };
  
  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast({ title: 'Name and email are required', variant: 'destructive' });
      return;
    }
    
    if (!editingUser && !userForm.password) {
      toast({ title: 'Password is required for new users', variant: 'destructive' });
      return;
    }
    
    if (userForm.password && userForm.password.length < 8) {
      toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    
    try {
      setSavingUser(true);
      if (editingUser) {
        const updateData = {
          name: userForm.name,
          email: userForm.email
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast({ title: 'User updated successfully!' });
      } else {
        await usersAPI.create(userForm);
        toast({ title: 'User created successfully!' });
      }
      setShowUserDialog(false);
      fetchUsers();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error saving user', 
        variant: 'destructive' 
      });
    } finally {
      setSavingUser(false);
    }
  };
  
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) return;
    
    try {
      await usersAPI.delete(user.id);
      toast({ title: 'User deleted successfully!' });
      fetchUsers();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error deleting user', 
        variant: 'destructive' 
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.updateContractTerms(terms);
      toast({ title: 'Settings saved successfully!' });
    } catch (error) {
      toast({ title: 'Error saving settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setTerms(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Settings</h1>
          <p className="text-gray-600">Manage your contract terms, company information, and users</p>
        </div>
        <Button asChild variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]" data-testid="settings-pos-menu-link">
          <a href="/settings/pos-menu">Manage POS Menu →</a>
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-white">
          <TabsTrigger value="company" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">
            <Building size={18} className="mr-2" />Company Settings
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white" data-testid="categories-tab">
            <FolderCog size={18} className="mr-2" />Categories
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white" data-testid="users-tab">
            <Users size={18} className="mr-2" />User Management
          </TabsTrigger>
          <TabsTrigger value="charge_templates" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white" data-testid="charge-templates-tab">
            <Sparkles size={18} className="mr-2" />Charge Templates
          </TabsTrigger>
          <TabsTrigger value="event_types" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white" data-testid="event-types-tab">
            <Calendar size={18} className="mr-2" />Event Types
          </TabsTrigger>
        </TabsList>
        
        {/* Company Settings Tab */}
        <TabsContent value="company">
          <div className="flex justify-end mb-4 gap-3">
            <Button onClick={fetchSettings} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
              <RefreshCw size={20} className="mr-2" />Reset
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="save-settings-btn">
              {saving ? <Loader2 size={20} className="mr-2 animate-spin" /> : <Save size={20} className="mr-2" />}
              Save Changes
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <Card className="border-none shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Building size={20} />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={terms.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Your Company Name"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={terms.company_tagline}
                onChange={(e) => handleChange('company_tagline', e.target.value)}
                placeholder="Catering & Events"
                data-testid="company-tagline-input"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={terms.company_address}
                onChange={(e) => handleChange('company_address', e.target.value)}
                placeholder="123 Main St&#10;City, Province A1A 1A1"
                rows={3}
                data-testid="company-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={terms.company_email}
                  onChange={(e) => handleChange('company_email', e.target.value)}
                  placeholder="events@company.com"
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={terms.company_phone}
                  onChange={(e) => handleChange('company_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="company-phone-input"
                />
              </div>
            </div>
            <div>
              <Label>Deposit Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={terms.deposit_percentage}
                onChange={(e) => handleChange('deposit_percentage', parseFloat(e.target.value))}
                data-testid="deposit-percentage-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card className="border-none shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <FileText size={20} />
              Contract Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>1. Deposit Terms</Label>
              <Textarea
                value={terms.deposit_note}
                onChange={(e) => handleChange('deposit_note', e.target.value)}
                rows={3}
                data-testid="deposit-note-input"
              />
            </div>
            <div>
              <Label>2. Guest Count Terms</Label>
              <Textarea
                value={terms.guest_count_note}
                onChange={(e) => handleChange('guest_count_note', e.target.value)}
                rows={3}
                data-testid="guest-count-note-input"
              />
            </div>
            <div>
              <Label>3. Cancellation Policy</Label>
              <Textarea
                value={terms.cancellation_note}
                onChange={(e) => handleChange('cancellation_note', e.target.value)}
                rows={3}
                data-testid="cancellation-note-input"
              />
            </div>
            <div>
              <Label>4. Changes Policy</Label>
              <Textarea
                value={terms.changes_note}
                onChange={(e) => handleChange('changes_note', e.target.value)}
                rows={3}
                data-testid="changes-note-input"
              />
            </div>
            <div>
              <Label>5. Liability Terms</Label>
              <Textarea
                value={terms.liability_note}
                onChange={(e) => handleChange('liability_note', e.target.value)}
                rows={3}
                data-testid="liability-note-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Terms */}
        <Card className="border-none shadow-lg lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-[#c9a227] to-[#c4a574] text-[#1e3a5f] rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon size={20} />
              Additional Terms (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div>
              <Label>Custom Terms & Notes</Label>
              <Textarea
                value={terms.additional_terms || ''}
                onChange={(e) => handleChange('additional_terms', e.target.value)}
                placeholder="Add any additional terms, notes, or special conditions that should appear on contracts..."
                rows={5}
                data-testid="additional-terms-input"
              />
              <p className="text-sm text-gray-500 mt-2">These will appear at the end of your contract's Terms & Conditions section.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card className="border-none shadow-lg mt-6">
        <CardHeader className="bg-gray-100 rounded-t-lg">
          <CardTitle className="text-[#1e3a5f]">Preview: How Terms Appear on Contracts</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="font-bold text-[#1e3a5f] text-lg mb-4">TERMS & CONDITIONS</h3>
            <div className="text-sm text-gray-700 space-y-3">
              <p><strong>1. Deposit:</strong> {terms.deposit_note}</p>
              <p><strong>2. Guest Count:</strong> {terms.guest_count_note}</p>
              <p><strong>3. Cancellation:</strong> {terms.cancellation_note}</p>
              <p><strong>4. Changes:</strong> {terms.changes_note}</p>
              <p><strong>5. Liability:</strong> {terms.liability_note}</p>
              {terms.additional_terms && (
                <p><strong>6. Additional:</strong> {terms.additional_terms}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
        
        {/* User Management Tab */}
        <TabsContent value="users">
          <div className="flex justify-between items-center mb-4">
            <p className="text-gray-600">Manage admin users who can access the system</p>
            <Button onClick={() => handleOpenUserDialog()} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-user-btn">
              <UserPlus size={20} className="mr-2" />Add User
            </Button>
          </div>
          
          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[#1e3a5f]" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {users.map(user => (
                <Card key={user.id} className="border-none shadow-lg" data-testid={`user-card-${user.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-bold text-lg">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1e3a5f]">{user.name}</h3>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        Admin
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleOpenUserDialog(user)}
                          className="text-[#1e3a5f] hover:bg-[#1e3a5f]/10"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      Added: {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
              
              {users.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white rounded-lg">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No users found. Add your first user to get started.</p>
                </div>
              )}
            </div>
          )}
        </TabsContent>
        
        {/* Categories Tab */}
        <TabsContent value="categories">
          <CategoryManagement />
        </TabsContent>

        {/* Custom Charge Templates Tab */}
        <TabsContent value="charge_templates">
          <CustomChargeTemplatesManagement />
        </TabsContent>

        {/* Event Types Tab */}
        <TabsContent value="event_types">
          <EventTypesManagement />
        </TabsContent>
      </Tabs>
      
      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information below.' : 'Enter details for the new admin user.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="John Smith"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  className="pr-10"
                  data-testid="user-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters</p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser} 
              disabled={savingUser}
              className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
              data-testid="save-user-btn"
            >
              {savingUser ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
